import dayjs from 'dayjs';
import Handsontable from 'handsontable';
import { lightenColor } from './colorUtils';
import { formatMetricValue, getCurrencySymbol, formatPercentageValue } from './formatters';

/**
 * Marka için grid verisini hazırlar
 * @param {number} brandId - Marka ID'si
 * @param {Array} apiData - API'den gelen veriler
 * @param {Object} metrics - Marka metrikleri
 * @param {Object} allMetricsData - Tüm metrik verileri
 * @param {Object} selectedMonth - Seçilen ay
 * @returns {Array} - Grid verisi
 */
export const prepareGridData = (brandId, apiData, metrics, allMetricsData, selectedMonth) => {
  if (!metrics[brandId] || metrics[brandId].length === 0) {
    return [];
  }
  
  // Önce metrikleri sırala ve sadece görünür (isVisible:true) olanları filtrele
  const sortedMetrics = [...metrics[brandId]]
    .filter(metric => metric.BrandMetric?.isVisible !== false) // isVisible:false olanları hariç tut
    .sort((a, b) => {
      const orderA = a.BrandMetric?.displayOrder || 0;
      const orderB = b.BrandMetric?.displayOrder || 0;
      return orderA - orderB;
    });
  
  // Hiç görünür metrik yoksa boş array döndür
  if (sortedMetrics.length === 0) {
    return [];
  }
  
  // Ayın tüm günlerini oluştur
  const daysInMonth = selectedMonth.daysInMonth();
  const newGridData = [];
  
  // İlişkili aylık metrik ID'lerini saklamak için geçici bir nesne
  const linkedMonthlyMetricIds = {};
  
  // Önce günlük verileri işleyelim ve linkedMonthlyMetricIds bilgilerini toplayalım
  const dailyRows = [];
  
  // Günlük değerleri ekle
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = dayjs(selectedMonth).date(day);
    const rowData = {
      date: currentDate.format('DD.MM.YYYY'),
      dateObj: currentDate,
      isMonthlyRow: false
    };

    // Her metrik için o güne ait değeri bul
    sortedMetrics.forEach(metric => {
      const metricId = metric.id;
      
      // API yanıtındaki veri yapısına göre uygun veriyi bul
      const dataForDay = apiData.find(
        item => 
          // Metrik ID kontrolü
          (item.Metric?.id === metricId || item.metricId === metricId) && 
          // Tarih kontrolü
          dayjs(item.date).date() === day
      );
      
      // İlişkili aylık metrik ID'sini sakla (API'den geliyorsa)
      if (dataForDay && dataForDay.linkedMonthlyMetricId) {
        linkedMonthlyMetricIds[metricId] = dataForDay.linkedMonthlyMetricId;
      }
      
      // Veri işlemleri - tipine göre değeri işle
      let value = dataForDay ? dataForDay.value : null;
      
      if (value !== null && value !== undefined) {
        // Para birimi için kuruşları kaldır
        if (metric.dataType === 'currency') {
          value = Math.round(parseFloat(value));
        }
        // String tipinde ise uzunluğunu sınırla
        else if (metric.dataType === 'text') {
          value = String(value).length > 42 ? String(value).substring(0, 42) + '...' : value;
        }
      }
      
      rowData[`metric_${metricId}`] = value;
    });
    
    dailyRows.push(rowData);
  }
  
  // Şimdi topladığımız linkedMonthlyMetricIds bilgilerini kullanarak aylık satırı oluşturalım
  const monthlyRow = {
    date: `${selectedMonth.format('MMMM YYYY')}`,
    dateObj: null,
    isMonthlyRow: true
  };
  
  // Her metrik için ilişkili aylık metrik değerini bul
  sortedMetrics.forEach(metric => {
    const metricId = metric.id;
    
    // Önce BrandMetric'ten, yoksa toplanan geçici nesneden al
    const linkedMonthlyMetricId = metric.BrandMetric?.linkedMonthlyMetricId || linkedMonthlyMetricIds[metricId];
    
    if (linkedMonthlyMetricId) {
      const monthlyMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === linkedMonthlyMetricId);
      if (monthlyMetric) {
        // Veri işlemleri - tipine göre değeri işle
        let value = monthlyMetric.value;
        
        if (value !== null && value !== undefined) {
          // Para birimi için kuruşları kaldır
          if (metric.dataType === 'currency') {
            value = Math.round(parseFloat(value));
          }
          // String tipinde ise uzunluğunu sınırla
          else if (metric.dataType === 'text') {
            value = String(value).length > 42 ? String(value).substring(0, 42) + '...' : value;
          }
        }
        
        monthlyRow[`metric_${metricId}`] = value;
      } else {
        monthlyRow[`metric_${metricId}`] = null;
      }
    } else {
      monthlyRow[`metric_${metricId}`] = null;
    }
  });
  
  // Aylık satırı en üste ekle
  newGridData.push(monthlyRow);
  
  // Ardından günlük satırları ekle
  newGridData.push(...dailyRows);
  
  return newGridData;
};

/**
 * Handsontable için sütunları oluşturur
 * @param {Array} metrics - Metrikler dizisi
 * @returns {Array} - Handsontable sütunları
 */
export const getColumns = (brandId, metrics) => {
  if (!metrics[brandId] || metrics[brandId].length === 0) {
    return [];
  }

  const sortedMetrics = [...metrics[brandId]].sort((a, b) => {
    const orderA = a.BrandMetric?.displayOrder || 0;
    const orderB = b.BrandMetric?.displayOrder || 0;
    return orderA - orderB;
  });

  // Metrik sütunlarını ekle
  const columns = [];
  sortedMetrics.forEach(metric => {
    columns.push({
      data: `metric_${metric.id}`,
      type: metric.dataType === 'text' ? 'text' : 'numeric',
      numericFormat: metric.dataType !== 'text' ? {
        pattern: '0,0.[00]',
        culture: 'tr-TR'
      } : undefined,
      readOnly: true,
      title: metric.description || metric.name || 'Metrik'
    });
  });

  return columns;
};

/**
 * Handsontable için hücre render fonksiyonu
 * @param {Object} metric - Metrik nesnesi
 * @param {number} brandId - Marka ID'si
 * @param {Object} allMetricsData - Tüm metrik verileri
 * @returns {Function} - Hücre render fonksiyonu
 */
export const getCellRenderer = (metric, brandId, allMetricsData) => {
  // Marka para birimini al
  const brandCurrency = allMetricsData[brandId]?.brandInfo?.currency || 'TRY';
  
  return function(instance, td, row, col, prop, value, cellProperties) {
    const isMonthlyRow = row === 0;
    
    // Para birimi için özel render
    if (metric.dataType === 'currency' && value !== null && value !== undefined && value !== '') {
      Handsontable.renderers.NumericRenderer.apply(this, arguments);
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Marka para birimine göre sembolü al ve ekle
        const currencySymbol = getCurrencySymbol(brandCurrency);
        td.textContent = Math.round(numValue).toLocaleString('tr-TR') + ' ' + currencySymbol;
      }
    } 
    // Yüzde için özel render
    else if (metric.dataType === 'percentage' && value !== null && value !== undefined && value !== '') {
      Handsontable.renderers.NumericRenderer.apply(this, arguments);
      td.textContent = formatPercentageValue(value);
    }
    // Metin için özel render
    else if (metric.dataType === 'text' && value) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      
      if (String(value).length > 42) {
        td.title = value;
        td.textContent = String(value).substring(0, 42) + '...';
      }
    }
    // Diğer tipler için varsayılan render
    else {
      Handsontable.renderers.NumericRenderer.apply(this, arguments);
    }
    
    // Aylık satır için özel stil
    if (isMonthlyRow) {
      td.style.backgroundColor = '#FFE599';
      td.style.fontWeight = 'bold';
      td.style.color = '#000';
    } 
    // Günlük satırlar için özel renklendirmeler - Sadece belirli metrikler için
    else if (!isMonthlyRow && value !== null && value !== undefined) {
      const metricId = metric.id;
      
      // Sadece belirli metrik ID'leri için karşılaştırma yap
      switch (metricId) {
        // ID 1 (satış) metriği için özel karşılaştırma
        case 1:
          // 10004 ID'li aylık metriği bul
          const salesCompareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10004);
          
          if (salesCompareMetric && salesCompareMetric.value !== null && salesCompareMetric.value !== undefined) {
            const currentValue = parseFloat(value);
            const compareValue = parseFloat(salesCompareMetric.value);
            
            if (!isNaN(currentValue) && !isNaN(compareValue)) {
              // Günlük değer aylık değerden küçükse kırmızı, değilse yeşil
              td.style.backgroundColor = currentValue < compareValue ? '#ff17001f' : '#52c41a29';
              td.style.color = '#000';
            }
          }
          break;
        
        // ID 2 (ziyaretçi) metriği için özel karşılaştırma
        case 2:
          // 10008 ID'li aylık metriği bul
          const visitorCompareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10008);
          
          if (visitorCompareMetric && visitorCompareMetric.value !== null && visitorCompareMetric.value !== undefined) {
            const currentValue = parseFloat(value);
            const compareValue = parseFloat(visitorCompareMetric.value);
            
            if (!isNaN(currentValue) && !isNaN(compareValue)) {
              // Günlük değer aylık değerden küçükse kırmızı, değilse yeşil
              td.style.backgroundColor = currentValue < compareValue ? '#ff17001f' : '#52c41a29';
              td.style.color = '#000';
            }
          }
          break;
          case 56:
            // 10056 ID'li aylık metriği bul
            const trendyolSalesCompareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10058);
            
            if (trendyolSalesCompareMetric && trendyolSalesCompareMetric.value !== null && trendyolSalesCompareMetric.value !== undefined) {
              const currentValue = parseFloat(value);
              const compareValue = parseFloat(trendyolSalesCompareMetric.value);
              
              if (!isNaN(currentValue) && !isNaN(compareValue)) {
                // Günlük değer aylık değerden küçükse kırmızı, değilse yeşil
                td.style.backgroundColor = currentValue < compareValue ? '#ff17001f' : '#52c41a29';
                td.style.color = '#000';
              }
            }
            break;
          case 66:
          // 10055 ID'li aylık metriği bul
          const trendyolRoiCompareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10055);
          
          if (trendyolRoiCompareMetric && trendyolRoiCompareMetric.value !== null && trendyolRoiCompareMetric.value !== undefined) {
            const currentValue = parseFloat(value);
            const compareValue = parseFloat(trendyolRoiCompareMetric.value);
            
            if (!isNaN(currentValue) && !isNaN(compareValue)) {
              // Günlük değer aylık değerden küçükse kırmızı, değilse yeşil
              td.style.backgroundColor = currentValue < compareValue ? '#ff17001f' : '#52c41a29';
              td.style.color = '#000';
            }
          }
          break;
          
        // Diğer metrik ID'leri için varsayılan işlem yok
        default:
          break;
      }
    }
    
    // Metrik rengi varsa ve başka bir renklendirme yoksa uygula
    if (metric.color && !td.style.backgroundColor) {
      td.style.backgroundColor = isMonthlyRow ? 
        lightenColor(metric.color, 0.95) : 
        lightenColor(metric.color, 0.85);
    }
    
    // Otomatik hesaplanan metrikler için stil
    if (metric.isAutoCalculated) {
      td.style.fontStyle = 'italic';
      if (!td.style.color || td.style.color === '#000') {
        td.style.color = '#666666';
      }
    }
  };
}; 